import { useMemo, useSyncExternalStore } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { allClips, isHighlightReelClip } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';

/**
 * The clip store holds every asset subtype, so reads have to be subtype-scoped. Highlight reels are
 * rendered by their own popover and live under their own route (/highlights); everything that says
 * "clips" — the clip list, prev/next navigation, the post composer's clip picker — means regular
 * clips and must not see reels.
 */
function useVisibleClipsBy(predicate: (clip: Clip) => boolean) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const allClipsStoreData = useSyncExternalStore(allClips.subscribe, allClips.getSnapshot);

  return useMemo(() => {
    const showAiEditsTestingClips = !!featureFlags[FeatureFlagKeys.Use_CL_Copy_Clip_To_Test_AiEdits];

    return allClipsStoreData.filter(clip => {
      // Hide clips that are marked as AI edits testing clips
      if (!showAiEditsTestingClips && clip.asset_metadata.is_copied_for_ai_edits_testing === true) {
        return false;
      }
      return predicate(clip);
    });
  }, [allClipsStoreData, featureFlags, predicate]);
}

const isRegularClip = (clip: Clip) => !isHighlightReelClip(clip);

export const useAllVisibleClips = () => {
  const allVisibleClips = useVisibleClipsBy(isRegularClip);

  return { allVisibleClips };
};

/**
 * Highlight reels only — the counterpart to `useAllVisibleClips`, backed by the same store.
 */
export const useAllVisibleReels = () => {
  const allVisibleReels = useVisibleClipsBy(isHighlightReelClip);

  return { allVisibleReels };
};
